// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"
import posthog from "posthog-js"
import { sanitizePathValue, scrubPosthogEvent } from "./lib/posthog_privacy.mjs"

const ATTRIBUTION_COOKIE = "kova_attribution"
const ATTRIBUTION_MAX_AGE = 60 * 60 * 24 * 180
const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "referrer_host",
  "landing_path",
  "captured_at"
]
const CLICK_CHANNELS = {
  gclid: { source: "google", medium: "cpc" },
  gbraid: { source: "google", medium: "cpc" },
  wbraid: { source: "google", medium: "cpc" },
  msclkid: { source: "bing", medium: "cpc" },
  fbclid: { source: "facebook", medium: "paid_social" },
  li_fat_id: { source: "linkedin", medium: "paid_social" },
  ttclid: { source: "tiktok", medium: "paid_social" },
  twclid: { source: "x", medium: "paid_social" }
}
let posthogInitialized = false
let lastAppliedPosthogConfig = null
let lastAttributionCaptureKey = null

captureAttribution({ allowReferrer: true })
applyPosthogConfig()

document.addEventListener("turbo:load", () => {
  captureAttribution({ allowReferrer: false })
  applyPosthogConfig()
})
window.addEventListener("kova:posthog-config", applyPosthogConfig)

function initializePosthog(config) {
  if (posthogInitialized || !config.apiKey) return

  posthog.init(config.apiKey, {
    api_host: config.host || "https://us.i.posthog.com",
    defaults: "2025-11-30", // Uses capture_pageview: 'history_change' for Turbo compatibility
    person_profiles: "identified_only",
    capture_pageleave: true,
    autocapture: false,
    disable_session_recording: true,
    save_campaign_params: false,
    save_referrer: false,
    before_send: scrubPosthogEvent
  })

  window.posthog = posthog
  posthogInitialized = true
}

function applyPosthogConfig() {
  const config = window.KOVA_POSTHOG_CONFIG || {}
  if (!config.apiKey) return

  initializePosthog(config)

  const configKey = JSON.stringify({
    reset: Boolean(config.reset),
    user: config.user || null
  })
  if (configKey === lastAppliedPosthogConfig) return

  if (config.reset) posthog.reset()
  if (config.user?.id) posthog.identify(config.user.id, config.user.properties || {}, config.user.setOnce || {})

  lastAppliedPosthogConfig = configKey
}

function captureAttribution({ allowReferrer = false } = {}) {
  const captureKey = `${window.location.href}|${allowReferrer ? document.referrer : ""}`
  if (captureKey === lastAttributionCaptureKey) return
  lastAttributionCaptureKey = captureKey

  const touch = buildCurrentTouch({ allowReferrer })
  if (!touch) return

  const existing = readAttributionCookie()
  const next = {
    first: existing.first || touch,
    latest: touch
  }

  writeAttributionCookie(next)
}

function buildCurrentTouch({ allowReferrer = false } = {}) {
  const url = new URL(window.location.href)
  const params = url.searchParams
  const touch = {
    landing_path: sanitizeLandingPath(url.pathname),
    captured_at: new Date().toISOString()
  }
  let hasSignal = false

  for (const key of ATTRIBUTION_KEYS) {
    if (!key.startsWith("utm_")) continue
    const value = sanitizeToken(params.get(key))
    if (value) {
      touch[key] = value
      hasSignal = true
    }
  }

  if (!touch.utm_source || !touch.utm_medium) {
    const clickChannel = inferredClickChannel(params)
    if (clickChannel) {
      touch.utm_source ||= clickChannel.source
      touch.utm_medium ||= clickChannel.medium
      hasSignal = true
    }
  }

  if (!hasSignal && allowReferrer) {
    const referrerHost = externalReferrerHost()
    if (referrerHost) {
      touch.referrer_host = referrerHost
      touch.utm_source ||= referrerHost
      touch.utm_medium ||= "referral"
      hasSignal = true
    }
  }

  if (!hasSignal && !readAttributionCookie().first) {
    touch.utm_source = "direct"
    touch.utm_medium = "none"
    hasSignal = true
  }

  return hasSignal ? compactTouch(touch) : null
}

function inferredClickChannel(params) {
  for (const key of Object.keys(CLICK_CHANNELS)) {
    if (params.has(key)) return CLICK_CHANNELS[key]
  }

  return null
}

function externalReferrerHost() {
  if (!document.referrer) return null

  try {
    const referrer = new URL(document.referrer)
    if (referrer.host === window.location.host) return null
    return sanitizeHost(referrer.hostname)
  } catch {
    return null
  }
}

function compactTouch(touch) {
  return ATTRIBUTION_KEYS.reduce((result, key) => {
    if (touch[key]) result[key] = touch[key]
    return result
  }, {})
}

function readAttributionCookie() {
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${ATTRIBUTION_COOKIE}=`))
  if (!cookie) return {}

  try {
    return JSON.parse(decodeURIComponent(cookie.split("=").slice(1).join("="))) || {}
  } catch {
    return {}
  }
}

function writeAttributionCookie(value) {
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(value))}; Path=/; Max-Age=${ATTRIBUTION_MAX_AGE}; SameSite=Lax${secure}`
}

function sanitizeToken(value) {
  if (!value) return null

  const sanitized = value.trim().replace(/[\u0000-\u001F\u007F]/g, "")
  if (!sanitized || /@|password|passwd|token|secret|session_id|code_challenge|authorization|bearer/i.test(sanitized)) return null
  if (/[?&=/\\]/.test(sanitized)) return null

  return sanitized.slice(0, 120)
}

function sanitizeHost(value) {
  if (!value) return null

  const sanitized = value.toLowerCase().replace(/^www\./, "")
  return /^[a-z0-9.-]+$/.test(sanitized) ? sanitized.slice(0, 120) : null
}

function sanitizeLandingPath(value) {
  if (!value || !value.startsWith("/")) return "/"

  return sanitizePathValue(value).slice(0, 200)
}
